/**
 * Idempotency key for submitQrOrder.
 *
 * One id per checkout attempt: re-submitting the SAME cart (double tap,
 * timeout retry, page reload) reuses the id, so the server replays the first
 * result instead of creating a second order. Any change to what is being
 * ordered produces a new id. Cleared after a confirmed success.
 */

const STORAGE_KEY = 'qr_order_request_id';

function newId(): string {
  const c: Crypto | undefined = typeof window !== 'undefined' ? window.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Must cover at least every field the server fingerprints (items, table,
 * order type, payment method) — otherwise a changed cart would reuse an old
 * id and be rejected.
 */
function signatureOf(order: Record<string, any>): string {
  return JSON.stringify({
    items: order.items,
    table: order.table_number ?? order.tableNumber ?? 0,
    order_type: order.order_type ?? 'dine_in',
    payment_method: order.payment_method ?? 'pending',
    address: order.customer_address ?? '',
    postcode: order.customer_postcode ?? '',
    branch: order.branch_id ?? '',
  });
}

export function getOrderRequestId(order: Record<string, any>): string {
  const signature = signatureOf(order);
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (stored && stored.signature === signature && typeof stored.id === 'string') {
      return stored.id;
    }
  } catch {
    /* storage unavailable — fall through to a fresh id */
  }
  const id = newId();
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ id, signature }));
  } catch {
    /* ignore */
  }
  return id;
}

export function clearOrderRequestId(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
